import React from 'react';
import { useTranslation } from 'react-i18next';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgButton from 'modules/Common/ui/CgButton';
import { cgToast } from 'modules/Common/ui/toast';
import { trackEmailContentCopied, trackEmailClientOpened } from 'helpers/mixpanel';

/**
 * Reusable modal component for previewing and sending support emails
 *
 * Originally built for trial extension requests, but now used for other
 * support flows (e.g. payment / upgrade requests) as well.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onHide - Callback to close the modal
 * @param {Object} props.emailPreview - Email content
 * @param {string} props.emailPreview.to - Recipient email
 * @param {string} props.emailPreview.subject - Email subject
 * @param {string} props.emailPreview.body - Email body (decoded)
 * @param {Object} props.organizationInfo - Organization details to display
 * @param {Object} props.userInfo - User details to display
 * @param {string} [props.title] - Modal title (defaults to trial extension title)
 * @param {string} [props.primaryCtaLabel] - Label for primary button (defaults to "Open in Email")
 * @returns {JSX.Element} The rendered TrialExtensionModal component
 */
const TrialExtensionModal = ({
  show,
  onHide,
  emailPreview = { to: '', subject: '', body: '' },
  organizationInfo = {},
  userInfo = {},
  title,
  primaryCtaLabel,
}) => {
  const { t } = useTranslation('common');
  const modalTitle = title ?? t('trialExtension.title', 'Trial Extension Request');
  const primaryCta = primaryCtaLabel ?? t('email.openInEmail', 'Open in Email');
  /**
   * Opens the system email client with pre-filled email
   */
  const handleOpenEmailClient = () => {
    if (!emailPreview.to) return;
    trackEmailClientOpened();
    const subjectParam = encodeURIComponent(emailPreview.subject || '');
    const bodyParam = encodeURIComponent(emailPreview.body || '');
    const mailtoUrl = `mailto:${emailPreview.to}?subject=${subjectParam}&body=${bodyParam}`;
    window.location.href = mailtoUrl;
  };

  /**
   * Copies text to clipboard with user notification
   */
  const handleCopyToClipboard = async (
    text,
    label = t('common:toasts.copied', 'Copied'),
    contentType = 'content',
  ) => {
    try {
      await navigator.clipboard.writeText(text || '');
      trackEmailContentCopied(contentType);
      cgToast.success(t('common:toasts.copied', 'Copied'), {
        description: t('common:toasts.copiedToClipboard', '{{label}} copied to clipboard', {
          label,
        }),
      });
    } catch (err) {
      cgToast.error(t('common:toasts.error', 'Error'), {
        description: t('common:toasts.failedToCopy', 'Failed to copy {{label}}', {
          label: label.toLowerCase(),
        }),
      });
    }
  };

  const footer = (
    <div className="flex justify-end gap-3 w-full">
      <CgButton
        variant="secondary"
        label={t('common:actions.close', 'Close')}
        onClick={onHide}
      />
      <CgButton
        variant="primary"
        icon="faPaperPlane"
        label={primaryCta}
        onClick={handleOpenEmailClient}
      />
    </div>
  );

  return (
    <CgDialog
      open={show}
      onOpenChange={(o) => !o && onHide()}
      title={modalTitle}
      size="lg"
      footer={footer}
    >
      <div className="p-2">
        {/* Organization & User Details */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-sm">
            <div className="flex justify-between mb-1">
              <span className="font-medium text-gray-700">{t('common:email.toLabel', 'To:')}</span>
              <span className="text-gray-900 break-all">{emailPreview.to}</span>
            </div>
            {Object.entries(organizationInfo).map(([key, value]) => (
              <div
                key={key}
                className="flex justify-between mb-1"
              >
                <span className="font-medium text-gray-700">{key}:</span>
                <span className="text-gray-900">{value}</span>
              </div>
            ))}
            {Object.entries(userInfo).map(([key, value]) => (
              <div
                key={key}
                className="flex justify-between mb-1"
              >
                <span className="font-medium text-gray-700">{key}:</span>
                <span className="text-gray-900">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-gray-700">
              {t('common:email.subject', 'Subject')}
            </label>
            <CgButton
              size="xs"
              variant="info"
              outlined
              icon="faCopy"
              label={t('common:email.copySubject', 'Copy Subject')}
              onClick={() =>
                handleCopyToClipboard(
                  emailPreview.subject,
                  t('common:email.subject', 'Subject'),
                  'subject',
                )
              }
            />
          </div>
          <input
            type="text"
            readOnly
            value={emailPreview.subject}
            className="w-full text-sm p-2 border rounded bg-white"
            onFocus={(e) => e.target.select()}
          />
        </div>

        {/* Body */}
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-gray-700">
              {t('common:email.message', 'Message')}
            </label>
            <CgButton
              size="xs"
              variant="info"
              outlined
              icon="faCopy"
              label={t('common:email.copyMessage', 'Copy Message')}
              onClick={() =>
                handleCopyToClipboard(
                  emailPreview.body,
                  t('common:email.message', 'Message'),
                  'message',
                )
              }
            />
          </div>
          <textarea
            readOnly
            value={emailPreview.body}
            rows={12}
            className="w-full text-sm p-2 border rounded bg-white whitespace-pre-wrap"
            onFocus={(e) => e.target.select()}
          />
        </div>
      </div>
    </CgDialog>
  );
};

export default TrialExtensionModal;
